import axios from "axios";
import { API_BASE_URL } from "./config";

// Single axios client for the whole app.
// - Prevents `undefined/api` when env vars are missing
// - Centralizes auth headers + 401 handling
// - Allows same-origin deployments ("/api")
const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      // Token is invalid/expired. Clear it so the app can redirect to login.
      localStorage.removeItem("token");
      window.dispatchEvent(new Event("auth:logout"));
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Categories
  getCategories: async () => {
    const response = await client.get(`/categories`);
    return response.data;
  },

  getCategoriesByTrack: async () => {
    const response = await client.get(`/categories/by-track`);
    return response.data;
  },

  getCategory: async (categoryId) => {
    const response = await client.get(`/categories/${categoryId}`);
    return response.data;
  },

  // Questions
  getQuestions: async (categoryId, limit = 5, scaffolding = false, quick = false) => {
    const response = await client.get(
      `/questions/${categoryId}?limit=${limit}&scaffolding=${scaffolding}&quick=${quick}`
    );
    return response.data;
  },

  generateQuestion: async (category, topic = null, scaffolding = false) => {
    const response = await client.post(`/questions/generate`, { category, topic, scaffolding });
    return response.data;
  },

  submitAnswer: async (questionId, selectedAnswer, correctAnswer, category, topic, isCorrect) => {
    const response = await client.post(`/questions/answer`, {
      question_id: questionId,
      selected_answer: selectedAnswer,
      correct_answer: correctAnswer,
      category: category,
      topic: topic,
      is_correct: isCorrect,
    });
    return response.data;
  },

  // User Tier & Usage
  getUserTier: async () => {
    const response = await client.get(`/user/tier`);
    return response.data;
  },

  // Progress
  getProgress: async () => {
    const response = await client.get(`/progress`);
    return response.data;
  },

  updateProgress: async (category, xpEarned, correctAnswers, totalQuestions) => {
    const response = await client.post(`/progress/update`, {
      category,
      xp_earned: xpEarned,
      correct_answers: correctAnswers,
      total_questions: totalQuestions,
    });
    return response.data;
  },

  // AI Tutor
  chatWithTutor: async (message, category = null) => {
    const response = await client.post(`/tutor/chat`, { message, category });
    return response.data;
  },

  getChatHistory: async () => {
    const response = await client.get(`/tutor/history`);
    return response.data;
  },

  // Leaderboard
  getLeaderboard: async (limit = 10) => {
    const response = await client.get(`/leaderboard?limit=${limit}`);
    return response.data;
  },

  // Membership
  getMembershipPlans: async () => {
    const response = await client.get(`/membership/plans`);
    return response.data;
  },

  getMembershipStatus: async () => {
    const response = await client.get(`/membership/status`);
    return response.data;
  },

  createCheckout: async (planId, originUrl) => {
    const response = await client.post(`/membership/checkout`, {
      plan_id: planId,
      origin_url: originUrl,
    });
    return response.data;
  },

  // ── User Settings (dyslexia, language, onboarding) ──
  getSettings: async () => {
    const response = await client.get(`/user/settings`);
    return response.data;
  },

  updateSettings: async (patch) => {
    const response = await client.patch(`/user/settings`, patch);
    return response.data;
  },

  // ── Test Readiness ──
  getReadiness: async (category = null) => {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    const response = await client.get(`/readiness${qs}`);
    return response.data;
  },

  // ── Interactive Lessons ──
  getLessonsUsage: async () => {
    const response = await client.get(`/lessons/usage`);
    return response.data;
  },

  listLessons: async (category = null) => {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    const response = await client.get(`/lessons${qs}`);
    return response.data;
  },

  startLesson: async (category, topic = null) => {
    const response = await client.post(`/lessons/start`, { category, topic }, { timeout: 60000 });
    return response.data;
  },

  getLesson: async (lessonId) => {
    const response = await client.get(`/lessons/${lessonId}`);
    return response.data;
  },

  completeLesson: async (lessonId) => {
    const response = await client.post(`/lessons/${lessonId}/complete`);
    return response.data;
  },

  // ── Archie Personalization ──
  archieRecommendation: async () => {
    const response = await client.get(`/archie/recommendation`);
    return response.data;
  },

  archieExplainWrong: async ({ question, userAnswer, correctAnswer, topic, category }) => {
    const response = await client.post(`/archie/explain-wrong`, {
      question,
      user_answer: userAnswer,
      correct_answer: correctAnswer,
      topic,
      category,
    });
    return response.data;
  },
};
